import React, { useEffect, useState } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Card, CardHeader, CardContent } from '../../../../components/ui/Card';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '../../../../components/ui/Dialog';
import Button from '../../../../components/ui/Button';
import AppIcon from '../../../../components/AppIcon';
import Pagination from '../../../../components/Pagination';
import { useTranslation } from 'react-i18next';
import { UserWorkExperienceInput, UserWorkExperienceView } from '../../../../models/employee';
import { addUserWorkExperience, deleteUserWorkExperience, editUserWorkExperience, getUserWorkExperiences } from '../../../../api/Employee/workExperience';
import WorkExperienceForm from './workExperienceForm';
import { formatDate } from '../../../../utils';

interface Props { userId: string; }

const WorkExperienceSection: React.FC<Props> = ({ userId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<UserWorkExperienceView[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [input, setInput] = useState<UserWorkExperienceInput>({
    id:0, userId, companyName:'', jobTitle:'', jobDescription:'', startDate:undefined, endDate:undefined, isCurrentJob:false, location:'', managerName:'', managerContact:''
  });

  const load = async () => { try { setLoading(true); const res = await getUserWorkExperiences(userId); setItems(res?.payload ?? []); setPage(1); } finally { setLoading(false); } };
  useEffect(() => { load(); }, [userId]);

  const openAdd = () => { setInput({ id:0, userId, companyName:'', jobTitle:'', jobDescription:'', startDate:undefined, endDate:undefined, isCurrentJob:false, location:'', managerName:'', managerContact:'' }); setAddOpen(true); };
  const add = async () => { setLoading(true); await addUserWorkExperience(input); setAddOpen(false); await load(); setLoading(false); };
  const openEdit = (w: UserWorkExperienceView) => { setInput({ id:w.id, userId, companyName:w.companyName, jobTitle:w.jobTitle, jobDescription:w.jobDescription, startDate:w.startDate, endDate:w.endDate, isCurrentJob:w.isCurrentJob, location:w.location, managerName:w.managerName, managerContact:w.managerContact }); setEditOpen(true); };
  const edit = async () => { setLoading(true); await editUserWorkExperience(input.id, input); setEditOpen(false); await load(); setLoading(false); };
  const openDelete = (w: UserWorkExperienceView) => { setInput({ id:w.id, userId, companyName:w.companyName, jobTitle:w.jobTitle, jobDescription:w.jobDescription, startDate:w.startDate, endDate:w.endDate, isCurrentJob:w.isCurrentJob, location:w.location, managerName:w.managerName, managerContact:w.managerContact }); setDeleteOpen(true); };
  const del = async () => { setLoading(true); await deleteUserWorkExperience(input.id); setDeleteOpen(false); await load(); setLoading(false); };

  return (
    <Card variant="elevated" className="mb-2">
      <CardHeader>
        <div className="flex items-center gap-2">
          <AppIcon name="Briefcase" className="text-primary" />
          <span className="text-muted-foreground font-semibold">{t('WORK EXPERIENCE')}</span>
        </div>
        <Button size="sm" iconName="Plus" onClick={openAdd} disabled={loading}>{t('Add')}</Button>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="p-2">
            {[...Array(3)].map((_, i) => <div key={i} className={`animate-pulse bg-muted h-8 rounded${i < 2 ? ' mb-1' : ''}`} />)}
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-center py-10">
            <AppIcon name="Briefcase" size={32} className="mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">{t('No work experience records found')}</p>
          </div>
        )}
        <div className="space-y-3">
          {items.slice((page - 1) * pageSize, page * pageSize).map(w => (
            <article key={w.id} className="bg-muted/50 border border-border/40 rounded-lg p-4 group">
              <div className="flex items-start justify-between gap-3">
                <div className="flex gap-3 min-w-0">
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center mt-0.5">
                    <AppIcon name="Briefcase" size={18} className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold text-foreground truncate">{w.companyName}</h4>
                    <p className="text-sm text-muted-foreground">
                      {[w.jobTitle, w.jobDescription].filter(Boolean).join(' \u00B7 ')}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground/70">
                      {(w.startDate || w.endDate) && (
                        <span>
                          {w.startDate ? formatDate(new Date(w.startDate)) : ''}
                          {w.startDate && w.endDate ? ' - ' : ''}
                          {w.endDate ? formatDate(new Date(w.endDate)) : (w.isCurrentJob ? t('Present') : '')}
                        </span>
                      )}
                      {w.location && (
                        <span className="inline-flex items-center gap-1">
                          <AppIcon name="MapPin" size={11} />
                          {w.location}
                        </span>
                      )}
                    </div>
                    {(w.managerName || w.managerContact) && (
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        {w.managerName && <span>{t('Manager Name')}: {w.managerName}</span>}
                        {w.managerName && w.managerContact && ' \u00B7 '}
                        {w.managerContact && <span>{w.managerContact}</span>}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <TooltipPrimitive.Provider delayDuration={300}>
                    <TooltipPrimitive.Root>
                      <TooltipPrimitive.Trigger asChild>
                        <Button variant="ghost" size="icon" className="text-primary h-7 w-7" onClick={() => openEdit(w)}>
                          <AppIcon name="Pencil" size={14} />
                        </Button>
                      </TooltipPrimitive.Trigger>
                      <TooltipPrimitive.Portal>
                        <TooltipPrimitive.Content className="rounded bg-foreground px-2 py-1 text-xs text-background shadow-md" sideOffset={5}>
                          {t('Edit')}
                          <TooltipPrimitive.Arrow className="fill-foreground" />
                        </TooltipPrimitive.Content>
                      </TooltipPrimitive.Portal>
                    </TooltipPrimitive.Root>
                    <TooltipPrimitive.Root>
                      <TooltipPrimitive.Trigger asChild>
                        <Button variant="ghost" size="icon" className="text-destructive h-7 w-7" onClick={() => openDelete(w)}>
                          <AppIcon name="Trash2" size={14} />
                        </Button>
                      </TooltipPrimitive.Trigger>
                      <TooltipPrimitive.Portal>
                        <TooltipPrimitive.Content className="rounded bg-foreground px-2 py-1 text-xs text-background shadow-md" sideOffset={5}>
                          {t('Delete')}
                          <TooltipPrimitive.Arrow className="fill-foreground" />
                        </TooltipPrimitive.Content>
                      </TooltipPrimitive.Portal>
                    </TooltipPrimitive.Root>
                  </TooltipPrimitive.Provider>
                </div>
              </div>
            </article>
          ))}
        </div>
        {!loading && items.length > pageSize && (
          <Pagination
            currentPage={page}
            totalPages={Math.ceil(items.length / pageSize)}
            onPageChange={setPage}
          />
        )}

        {/* Add Dialog */}
        <Dialog open={addOpen} onOpenChange={(isOpen) => { if (!isOpen) setAddOpen(false); }}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>{t('Add Work Experience')}</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <WorkExperienceForm workExperienceInput={input} setWorkExperienceInput={setInput} loading={loading} t={t} />
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>{t('Cancel')}</Button>
              <Button loading={loading} onClick={add}>{t('Add')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={(isOpen) => { if (!isOpen) setEditOpen(false); }}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>{t('Edit Work Experience')}</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <WorkExperienceForm workExperienceInput={input} setWorkExperienceInput={setInput} loading={loading} t={t} />
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>{t('Cancel')}</Button>
              <Button loading={loading} onClick={edit}>{t('Save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteOpen} onOpenChange={(isOpen) => { if (!isOpen) setDeleteOpen(false); }}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>{t('Delete Work Experience')}</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <p>{t('Are you sure you want to delete this work experience?')}</p>
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteOpen(false)}>{t('Cancel')}</Button>
              <Button variant="destructive" loading={loading} onClick={del}>{t('Delete')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default WorkExperienceSection;
